import { Combobox } from "@base-ui/react/combobox";
import { Select as BaseSelect } from "@base-ui/react/select";
import { Check, ChevronDown, CircleX, LoaderCircle } from "lucide-react";
import { useState, type CSSProperties, type FocusEventHandler, type ReactNode, type Ref } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "./cn";
import { FOCUS_RING } from "./focus";
import {
  controlBox,
  controlHeight,
  INNER_INPUT,
  useControlDefaults,
  type ControlSize,
  type ControlStatus,
} from "./control";
import { POPUP_TRANSITION, POPUP_Z_INDEX } from "./popup";
import { matchesSearch, optionText } from "./select-search";

export type SelectValue = string | number | boolean;

export interface SelectOption<V extends SelectValue = string> {
  value: V;
  label: ReactNode;
  /** Text to match and display when `label` is not a plain string (used by `showSearch`). */
  searchText?: string;
  disabled?: boolean;
}

export interface SelectProps<V extends SelectValue = string> {
  value?: V | null;
  defaultValue?: V;
  /** antd semantics: clearing passes `undefined` (typed as `V` so existing setters keep compiling). */
  onChange?: (value: V) => void;
  onBlur?: FocusEventHandler<HTMLElement>;
  options?: readonly SelectOption<V>[];
  placeholder?: ReactNode;
  allowClear?: boolean;
  /** Type-to-filter (Base UI `Combobox`); matches option labels case-insensitively. */
  showSearch?: boolean;
  loading?: boolean;
  disabled?: boolean;
  size?: ControlSize;
  status?: ControlStatus;
  notFoundContent?: ReactNode;
  id?: string;
  name?: string;
  ref?: Ref<HTMLElement>;
  className?: string;
  style?: CSSProperties;
  /** Popup class. */
  popupClassName?: string;
  "aria-label"?: string;
}

const POPUP = cn(
  "max-h-[min(20rem,var(--available-height))] min-w-[var(--anchor-width)] overflow-y-auto rounded-lg bg-noa-elevated p-1 text-sm text-noa shadow-lg shadow-noa-shadow outline-none",
  POPUP_TRANSITION
);

const ITEM = cn(
  "flex cursor-pointer items-center gap-2 rounded px-3 py-[5px] outline-none select-none",
  "data-[highlighted]:bg-noa-hover data-[selected]:bg-noa-primary-subtle data-[selected]:font-semibold",
  "data-[disabled]:cursor-not-allowed data-[disabled]:text-noa-disabled"
);

const ICON_BUTTON = cn(
  FOCUS_RING,
  "inline-flex shrink-0 cursor-pointer rounded-sm border-0 bg-transparent p-0 text-noa-muted hover:text-noa"
);

/** Single-value select (antd `Select`). For free-form lists (`mode="tags"`), use `TagsInput`. */
export function Select<V extends SelectValue = string>(props: SelectProps<V>) {
  return props.showSearch ? <SearchSelect {...props} /> : <PlainSelect {...props} />;
}

function PlainSelect<V extends SelectValue>(allProps: SelectProps<V>) {
  const {
    value,
    defaultValue,
    onChange,
    onBlur,
    options = [],
    placeholder,
    allowClear,
    loading,
    disabled,
    size: sizeProp,
    status: statusProp,
    notFoundContent,
    id: idProp,
    name,
    ref,
    className,
    style,
    popupClassName,
    "aria-label": ariaLabel,
  } = allProps;
  const { t } = useTranslation();
  // antd treats a present-but-undefined `value` (e.g. an empty RHF field) as controlled-empty.
  const controlled = "value" in allProps;
  const { size, status, id, aria } = useControlDefaults({ size: sizeProp, status: statusProp, id: idProp });
  // Uncontrolled selects keep their own value so the clear icon can show and actually reset it.
  const [internal, setInternal] = useState<V | null>(defaultValue ?? null);
  const current = controlled ? (value ?? null) : internal;
  const hasValue = current !== null;
  const select = (next: V | null) => {
    if (!controlled) setInternal(next);
    onChange?.((next ?? undefined) as V);
  };

  return (
    <BaseSelect.Root<V>
      value={current}
      onValueChange={select}
      items={options.map((option) => ({ value: option.value, label: option.label }))}
      disabled={disabled}
      name={name}
      id={id}
      modal={false}
    >
      <BaseSelect.Trigger
        ref={ref as Ref<HTMLButtonElement>}
        {...aria}
        aria-label={ariaLabel}
        onBlur={onBlur}
        onKeyDown={(event) => {
          // The visible clear icon is pointer-only (a button can't nest a tab stop), so offer the keyboard path here.
          if (!allowClear || !hasValue || disabled || (event.key !== "Backspace" && event.key !== "Delete")) return;
          event.preventDefault();
          select(null);
        }}
        className={cn(
          "group/select inline-flex w-full shrink-0 cursor-pointer items-center gap-1 text-left outline-none",
          controlBox(status, disabled),
          controlHeight[size],
          className
        )}
        style={style}
      >
        <BaseSelect.Value
          placeholder={placeholder}
          className="min-w-0 flex-1 truncate data-[placeholder]:text-noa-disabled"
        />
        {loading && <LoaderCircle className="size-3.5 shrink-0 animate-spin text-noa-muted" />}
        {allowClear && hasValue && !disabled && (
          <span
            aria-hidden="true"
            title={t("common:clear")}
            // Base UI opens the popup on mousedown, so stopping pointerdown alone is not enough.
            onPointerDown={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              select(null);
            }}
            className={cn(ICON_BUTTON, "hidden group-hover/select:inline-flex")}
          >
            <CircleX className="size-3.5" />
          </span>
        )}
        <BaseSelect.Icon className="inline-flex shrink-0 text-noa-muted">
          <ChevronDown className="size-3.5" />
        </BaseSelect.Icon>
      </BaseSelect.Trigger>
      <BaseSelect.Portal>
        <BaseSelect.Positioner sideOffset={4} alignItemWithTrigger={false} className={POPUP_Z_INDEX}>
          <BaseSelect.Popup className={cn(POPUP, popupClassName)}>
            <BaseSelect.List>
              {options.length === 0 && (
                <div className="px-3 py-2 text-noa-muted">{notFoundContent ?? t("common:noDataFound")}</div>
              )}
              {options.map((option) => (
                <BaseSelect.Item
                  key={String(option.value)}
                  value={option.value}
                  disabled={option.disabled}
                  className={ITEM}
                >
                  <BaseSelect.ItemText className="min-w-0 flex-1 truncate">{option.label}</BaseSelect.ItemText>
                </BaseSelect.Item>
              ))}
            </BaseSelect.List>
          </BaseSelect.Popup>
        </BaseSelect.Positioner>
      </BaseSelect.Portal>
    </BaseSelect.Root>
  );
}

function SearchSelect<V extends SelectValue>(allProps: SelectProps<V>) {
  const {
    value,
    defaultValue,
    onChange,
    onBlur,
    options = [],
    placeholder,
    allowClear,
    loading,
    disabled,
    size: sizeProp,
    status: statusProp,
    notFoundContent,
    id: idProp,
    name,
    ref,
    className,
    style,
    popupClassName,
    "aria-label": ariaLabel,
  } = allProps;
  const { t } = useTranslation();
  // antd treats a present-but-undefined `value` (e.g. an empty RHF field) as controlled-empty.
  const controlled = "value" in allProps;
  const { size, status, id, aria } = useControlDefaults({ size: sizeProp, status: statusProp, id: idProp });
  const find = (target: V | null | undefined) => options.find((option) => option.value === target) ?? null;

  return (
    <Combobox.Root<SelectOption<V>>
      items={[...options]}
      {...(controlled ? { value: find(value) } : {})}
      defaultValue={find(defaultValue)}
      onValueChange={(next) => onChange?.((next?.value ?? undefined) as V)}
      itemToStringLabel={optionText}
      filter={matchesSearch}
      isItemEqualToValue={(item, selected) => item.value === selected.value}
      disabled={disabled}
      name={name}
      id={id}
      autoHighlight
    >
      <Combobox.InputGroup
        className={cn(
          "inline-flex w-full min-w-0 shrink-0 items-center gap-1",
          controlBox(status, disabled),
          controlHeight[size],
          className
        )}
        style={style}
      >
        <Combobox.Input
          ref={ref as Ref<HTMLInputElement>}
          {...aria}
          placeholder={typeof placeholder === "string" ? placeholder : undefined}
          aria-label={ariaLabel}
          onBlur={onBlur}
          className={cn(INNER_INPUT, "h-full")}
        />
        {loading && <LoaderCircle className="size-3.5 shrink-0 animate-spin text-noa-muted" />}
        {allowClear && (
          <Combobox.Clear aria-label={t("common:clear")} className={ICON_BUTTON}>
            <CircleX className="size-3.5" />
          </Combobox.Clear>
        )}
        <Combobox.Trigger className={ICON_BUTTON}>
          <ChevronDown className="size-3.5" />
        </Combobox.Trigger>
      </Combobox.InputGroup>
      <Combobox.Portal>
        <Combobox.Positioner sideOffset={4} className={POPUP_Z_INDEX}>
          <Combobox.Popup className={cn(POPUP, popupClassName)}>
            <Combobox.Empty>
              <div className="px-3 py-2 text-noa-muted">{notFoundContent ?? t("common:noDataFound")}</div>
            </Combobox.Empty>
            <Combobox.List>
              {(option: SelectOption<V>) => (
                <Combobox.Item key={String(option.value)} value={option} disabled={option.disabled} className={ITEM}>
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  <Combobox.ItemIndicator className="inline-flex text-noa-primary">
                    <Check className="size-3.5" />
                  </Combobox.ItemIndicator>
                </Combobox.Item>
              )}
            </Combobox.List>
          </Combobox.Popup>
        </Combobox.Positioner>
      </Combobox.Portal>
    </Combobox.Root>
  );
}
